import { useMemo } from 'react'
import {
  daysUntil,
  formatBytes,
  formatDate,
  formatNumber,
  formatPercent,
  formatRelative,
  formatSarText,
  type DateStyle,
} from '@/lib/format'
import { useLocale } from './useLocale'

/**
 * Formatters bound to the current locale:
 *   const f = useFormat()
 *   f.date(server.lastCheckInAt)            25 Oct 2026
 *   f.date(x, 'datetime')                   25 Oct 2026, 14:05
 *   f.relative(server.lastCheckInAt)        3 minutes ago
 *   f.number(1200)                          1,200
 *   f.bytes(1_610_612_736)                  1.5 GB
 *   f.percent(0.15)                         15%
 * Money is a component: <Sar amount={69} /> from '@/lib/format'.
 */
export function useFormat() {
  const { locale } = useLocale()
  return useMemo(
    () => ({
      locale,
      number: (n: number, opts?: Intl.NumberFormatOptions) => formatNumber(n, locale, opts),
      date: (d: string | number | Date, style?: DateStyle) => formatDate(d, locale, style),
      relative: (d: string | number | Date) => formatRelative(d, locale),
      daysUntil: (d: string | number | Date) => daysUntil(d),
      bytes: (b: number) => formatBytes(b, locale),
      percent: (f: number, digits?: number) => formatPercent(f, locale, digits),
      sarText: (amount: number) => formatSarText(amount, locale),
    }),
    [locale],
  )
}
